import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";

async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

type PageState = "loading" | "invalid" | "form" | "success";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [customerId, setCustomerId] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Validate token on mount
  useEffect(() => {
    if (!token) { setPageState("invalid"); return; }

    const verifyToken = async () => {
      const { data } = await supabase
        .from("customers")
        .select("id, email, password_reset_expires")
        .eq("password_reset_token", token)
        .maybeSingle();

      if (!data) { setPageState("invalid"); return; }

      const expires = new Date(data.password_reset_expires).getTime();
      if (Date.now() > expires) { setPageState("invalid"); return; }

      setCustomerId(data.id);
      setCustomerEmail(data.email ?? "");
      setPageState("form");
    };

    verifyToken();
  }, [token]);

  const handleReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      setPasswordError("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("كلمتا المرور غير متطابقتين");
      return;
    }
    setPasswordError("");
    setIsLoading(true);

    const hash = await hashPassword(newPassword);

    const { error } = await supabase
      .from("customers")
      .update({
        password_hash: hash,
        password_reset_token: null,
        password_reset_expires: null,
      })
      .eq("id", customerId);

    setIsLoading(false);

    if (error) {
      setPasswordError("حدث خطأ أثناء الحفظ، حاول مرة أخرى");
      return;
    }

    setPageState("success");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-16" dir="rtl">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-amber-400/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img
            src="https://public.readdy.ai/ai/img_res/3f4c2435-201e-437d-afea-0ab12a1a0054.png"
            alt="الإبداع"
            className="w-16 h-16 object-contain mb-3"
          />
          <h1 className="text-white text-2xl font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
            متجر الإبداع
          </h1>
          <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {pageState === "loading" && "جاري التحقق..."}
            {pageState === "invalid" && "رابط غير صالح"}
            {pageState === "form" && "إعادة تعيين كلمة المرور"}
            {pageState === "success" && "تم تغيير كلمة المرور"}
          </p>
        </div>

        <div className="bg-[#111] border border-white/8 rounded-3xl p-8">

          {/* Loading */}
          {pageState === "loading" && (
            <div className="flex flex-col items-center justify-center py-10 gap-4">
              <div className="w-12 h-12 flex items-center justify-center">
                <div className="w-10 h-10 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-400 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>جاري التحقق من الرابط...</p>
            </div>
          )}

          {/* Invalid token */}
          {pageState === "invalid" && (
            <div className="flex flex-col items-center gap-5 py-4">
              <div className="w-20 h-20 flex items-center justify-center bg-red-400/10 rounded-full">
                <i className="ri-error-warning-line text-red-400 text-4xl"></i>
              </div>
              <div className="text-center">
                <p className="text-white font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif" }}>الرابط غير صالح أو منتهي</p>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  رابط إعادة التعيين انتهت صلاحيته أو تم استخدامه بالفعل.<br />
                  يُرجى طلب رابط جديد.
                </p>
              </div>
              <button
                onClick={() => navigate("/auth")}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-arrow-right-line text-base"></i>
                طلب رابط جديد
              </button>
            </div>
          )}

          {/* Form */}
          {pageState === "form" && (
            <div className="flex flex-col gap-5">
              <div className="text-center mb-1">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/10 rounded-full mx-auto mb-3">
                  <i className="ri-lock-password-line text-amber-400 text-2xl"></i>
                </div>
                <p className="text-white font-black text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>أدخل كلمة مرورك الجديدة</p>
                {customerEmail && (
                  <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif", direction: "ltr" }}>
                    {customerEmail}
                  </p>
                )}
              </div>

              {/* New password */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  كلمة المرور الجديدة <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleReset()}
                    placeholder="6 أحرف على الأقل"
                    className={`w-full bg-[#1a1a1a] border ${passwordError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 pl-12`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showNew ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              {/* Confirm password */}
              <div>
                <label className="text-gray-400 text-xs font-medium block mb-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  تأكيد كلمة المرور <span className="text-amber-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleReset()}
                    placeholder="أعد إدخال كلمة المرور"
                    className={`w-full bg-[#1a1a1a] border ${passwordError ? "border-red-500/60" : "border-white/10"} rounded-2xl px-4 py-4 text-white text-sm outline-none focus:border-amber-400/50 transition-colors placeholder-gray-600 pl-12`}
                    style={{ fontFamily: "'Cairo', sans-serif" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors cursor-pointer w-5 h-5 flex items-center justify-center"
                  >
                    <i className={showConfirm ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    <i className="ri-error-warning-line"></i>{passwordError}
                  </p>
                )}
              </div>

              {/* Password strength hint */}
              <div className="bg-white/3 border border-white/8 rounded-xl px-4 py-3 flex items-start gap-3">
                <i className="ri-shield-check-line text-amber-400 text-base mt-0.5 flex-shrink-0"></i>
                <div>
                  <p className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>نصيحة لكلمة مرور قوية:</p>
                  <p className="text-gray-600 text-xs mt-1 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    استخدم مزيجاً من الحروف والأرقام والرموز — لا تستخدم معلومات شخصية
                  </p>
                </div>
              </div>

              <button
                onClick={handleReset}
                disabled={isLoading}
                className="w-full bg-amber-400 hover:bg-amber-300 disabled:opacity-60 disabled:cursor-not-allowed text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line text-base"></i>
                    حفظ كلمة المرور الجديدة
                  </>
                )}
              </button>
            </div>
          )}

          {/* Success */}
          {pageState === "success" && (
            <div className="flex flex-col items-center gap-5 py-4">
              <div className="w-20 h-20 flex items-center justify-center bg-green-400/10 rounded-full">
                <i className="ri-checkbox-circle-line text-green-400 text-4xl"></i>
              </div>
              <div className="text-center">
                <p className="text-white font-black text-lg" style={{ fontFamily: "'Cairo', sans-serif" }}>تم تغيير كلمة المرور!</p>
                <p className="text-gray-400 text-sm mt-2 leading-relaxed" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  كلمة مرورك الجديدة محفوظة بنجاح.<br />
                  يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة.
                </p>
              </div>
              <button
                onClick={() => navigate("/auth")}
                className="w-full bg-amber-400 hover:bg-amber-300 text-black font-black py-4 rounded-2xl text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ fontFamily: "'Cairo', sans-serif" }}
              >
                <i className="ri-login-circle-line text-base"></i>
                تسجيل الدخول الآن
              </button>
            </div>
          )}

        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <a href="/" className="text-gray-600 hover:text-gray-400 text-sm transition-colors cursor-pointer" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-arrow-right-line ml-1"></i>
            العودة للمتجر
          </a>
        </div>
      </div>
    </div>
  );
}
